// src/components/ExpenseListScreen.jsx
// Lista de despesas premium: GET/DELETE via /psy/payment, busca, filtros, ordenação,
// métricas, skeleton, edição e modal de exclusão.
// Filtra despesas por clienteId/clientId === "despesa".
// Edit abre /finance/expense/edit com state.expense.
// Padrões: mostra erro real do backend e atualiza token via x-new-token.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import AppSidebar from "../components/AppSidebar";
import Logo from "../assets/logoClaro.webp";

import "../sass/pages/expenseList.scss";

const API_BASE = (
  process.env.REACT_APP_API_URL ||
  "https://api.psyassist.com.br"
).replace(/\/$/, "");

const AUTH_KEYS = {
  TOKEN: "psy_token",
  USER_ID: "psy_id",
  USER_NAME: "psy_usuario",
};

const NOTICE_TONE = {
  success: "success",
  warning: "warning",
  info: "info",
  neutral: "neutral",
};

function getNoticeTitle(type) {
  if (type === "success") return "Tudo certo";
  if (type === "warning") return "Atenção";
  if (type === "info") return "Aviso";
  return "Mensagem";
}

const CATEGORY_META = {
  Aluguel: {
    icon: "🏠",
    label: "Aluguel",
    tone: "blue",
  },
  Equipamentos: {
    icon: "🖥️",
    label: "Equipamentos",
    tone: "violet",
  },
  Assinaturas: {
    icon: "🔁",
    label: "Assinaturas",
    tone: "cyan",
  },
  Marketing: {
    icon: "📣",
    label: "Marketing",
    tone: "amber",
  },
  Outros: {
    icon: "✨",
    label: "Outros",
    tone: "slate",
  },
};

const SORT_OPTIONS = [
  { value: "date-desc", label: "Mais recentes" },
  { value: "date-asc", label: "Mais antigas" },
  { value: "value-desc", label: "Maior valor" },
  { value: "value-asc", label: "Menor valor" },
  { value: "next-due", label: "Próximo vencimento" },
  { value: "installments-desc", label: "Mais parcelas" },
];

const readAuth = (key) =>
  sessionStorage.getItem(key) || localStorage.getItem(key) || "";

const saveAuthToken = (token) => {
  if (!token) return;
  localStorage.setItem(AUTH_KEYS.TOKEN, token);
  sessionStorage.setItem(AUTH_KEYS.TOKEN, token);
};

function normalizeText(value = "") {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function safeNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function moneyBR(value) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(safeNumber(value));
}

function parseDate(value) {
  if (!value) return null;

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }

  const raw = String(value).trim();

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [year, month, day] = raw.split("-").map(Number);
    const date = new Date(year, month - 1, day);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDateBR(value) {
  const date = parseDate(value);

  if (!date) return "Não informado";

  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

function formatMonthBR(value) {
  const date = parseDate(value);

  if (!date) return "Sem data";

  return new Intl.DateTimeFormat("pt-BR", {
    month: "short",
    year: "numeric",
  })
    .format(date)
    .replace(".", "");
}

function getTimeBR(dateLike) {
  const date = parseDate(dateLike);

  if (!date) return "--:--";

  return new Intl.DateTimeFormat("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function isCurrentMonth(value) {
  const date = parseDate(value);

  if (!date) return false;

  const now = new Date();

  return date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth();
}

function isSameOrAfterToday(value) {
  const date = parseDate(value);

  if (!date) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const compare = new Date(date);
  compare.setHours(0, 0, 0, 0);

  return compare.getTime() >= today.getTime();
}

function getExpenseId(expense) {
  return expense?._id || expense?.id || expense?.paymentId || "";
}

function getExpenseValue(expense) {
  return safeNumber(expense?.valorTotal ?? expense?.total ?? expense?.valor ?? 0);
}

function getExpenseCategory(expense) {
  return String(expense?.servicoId || expense?.categoria || expense?.category || "Outros").trim() || "Outros";
}

function getExpenseDescription(expense) {
  return String(expense?.descricao || expense?.description || "").trim();
}

function getExpenseDate(expense) {
  return (
    expense?.dataCadastro ||
    expense?.createdAt ||
    expense?.data ||
    expense?.updatedAt ||
    null
  );
}

function getExpenseInstallments(expense) {
  return Array.isArray(expense?.parcelas) ? expense.parcelas : [];
}

function getInstallmentDueDate(installment) {
  return (
    installment?.vencimento ||
    installment?.dataVencimento ||
    installment?.dueDate ||
    null
  );
}

function getInstallmentValue(installment) {
  return safeNumber(installment?.valor ?? installment?.valorParcela ?? 0);
}

function getInstallmentStatus(installment) {
  return normalizeText(installment?.status || installment?.situacao || installment?.situacaoParcela || "");
}

function isInstallmentPaid(installment) {
  const status = getInstallmentStatus(installment);

  return (
    installment?.pago === true ||
    installment?.baixado === true ||
    status === "pago" ||
    status === "paga" ||
    status === "baixado" ||
    status === "baixada" ||
    status === "recebido" ||
    status === "recebida"
  );
}

function getNextInstallment(expense) {
  const installments = getExpenseInstallments(expense)
    .map((installment, index) => ({
      ...installment,
      _index: index,
      _dueDate: getInstallmentDueDate(installment),
      _dateObj: parseDate(getInstallmentDueDate(installment)),
      _paid: isInstallmentPaid(installment),
    }))
    .filter((installment) => installment._dateObj);

  if (!installments.length) return null;

  const ordered = [...installments].sort((a, b) => a._dateObj - b._dateObj);
  const openInstallments = ordered.filter((installment) => !installment._paid);

  return (
    openInstallments.find((installment) => isSameOrAfterToday(installment._dueDate)) ||
    openInstallments[0] ||
    ordered.find((installment) => isSameOrAfterToday(installment._dueDate)) ||
    ordered[ordered.length - 1]
  );
}

function getNextDueTime(expense) {
  const next = getNextInstallment(expense);
  const date = parseDate(getInstallmentDueDate(next));
  return date ? date.getTime() : Number.MAX_SAFE_INTEGER;
}

function getCategoryMeta(category) {
  const exact = CATEGORY_META[category];

  if (exact) return exact;

  const normalized = normalizeText(category);
  const foundKey = Object.keys(CATEGORY_META).find(
    (key) => normalizeText(key) === normalized
  );

  return foundKey ? CATEGORY_META[foundKey] : CATEGORY_META.Outros;
}

function getExpenseSearchText(expense) {
  const installments = getExpenseInstallments(expense);
  const nextInstallment = getNextInstallment(expense);

  return normalizeText(
    [
      getExpenseCategory(expense),
      getExpenseDescription(expense),
      moneyBR(getExpenseValue(expense)),
      formatDateBR(getExpenseDate(expense)),
      formatMonthBR(getExpenseDate(expense)),
      String(installments.length || ""),
      nextInstallment ? formatDateBR(getInstallmentDueDate(nextInstallment)) : "",
      nextInstallment ? moneyBR(getInstallmentValue(nextInstallment)) : "",
    ]
      .filter(Boolean)
      .join(" ")
  );
}

async function readErrorMessage(response) {
  try {
    const contentType = response.headers.get("content-type") || "";

    if (contentType.includes("application/json")) {
      const json = await response.json();
      return json?.message || json?.error || JSON.stringify(json);
    }

    const text = await response.text();
    return text || `HTTP ${response.status}`;
  } catch {
    return `HTTP ${response.status}`;
  }
}

function normalizeApiList(data) {
  if (Array.isArray(data)) return data;

  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.payments)) return data.payments;
  if (Array.isArray(data?.pagamentos)) return data.pagamentos;
  if (Array.isArray(data?.data)) return data.data;

  return [];
}

function isExpenseRecord(item) {
  const clienteId = item?.clienteId;
  const clientId = item?.clientId;

  return (
    clienteId === "despesa" ||
    clientId === "despesa" ||
    item?.tipo === "despesa" ||
    item?.type === "expense"
  );
}


function NoticeBanner({ notice, onClose }) {
  if (!notice) return null;

  const tone = NOTICE_TONE[notice.type] || NOTICE_TONE.neutral;

  return (
    <section
      className={`explist-notice explist-notice--${tone}`}
      role="status"
      aria-live="polite"
    >
      <div className="explist-notice__icon" aria-hidden="true">
        {tone === "success" ? "✓" : tone === "warning" ? "!" : "i"}
      </div>

      <div className="explist-notice__body">
        <strong>{notice.title || getNoticeTitle(tone)}</strong>
        <span>{notice.message}</span>
      </div>

      {notice.actionLabel && typeof notice.onAction === "function" ? (
        <button
          className="explist-notice__action"
          type="button"
          onClick={notice.onAction}
        >
          {notice.actionLabel}
        </button>
      ) : null}

      <button
        className="explist-notice__close"
        type="button"
        onClick={onClose}
        aria-label="Fechar aviso"
      >
        ×
      </button>
    </section>
  );
}

function ConfirmModal({
  open,
  title,
  message,
  detail,
  loading,
  onClose,
  onConfirm,
}) {
  if (!open) return null;

  return (
    <div className="explist-modal-overlay" role="dialog" aria-modal="true">
      <div className="explist-modal">
        <div className="explist-modal__icon">!</div>

        <div className="explist-modal__body">
          <h2>{title}</h2>
          <p>{message}</p>

          {detail ? <strong>{detail}</strong> : null}
        </div>

        <div className="explist-modal__actions">
          <button
            className="explist-btn explist-btn--ghost-dark"
            type="button"
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </button>

          <button
            className="explist-btn explist-btn--danger"
            type="button"
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? "Excluindo..." : "Excluir despesa"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ExpenseSkeleton() {
  return (
    <div className="explist-skeleton-list" aria-hidden="true">
      {Array.from({ length: 5 }).map((_, index) => (
        <div className="explist-skeleton-card" key={`expense-skeleton-${index}`}>
          <span className="explist-skeleton-icon" />
          <span className="explist-skeleton-lines">
            <i />
            <i />
            <i />
          </span>
          <span className="explist-skeleton-actions" />
        </div>
      ))}
    </div>
  );
}

export default function ExpenseListScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const noticeRef = useRef(null);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notice, setNotice] = useState(null);
  const [items, setItems] = useState([]);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [query, setQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("Todas");
  const [sortBy, setSortBy] = useState("date-desc");

  const [confirmDel, setConfirmDel] = useState({
    open: false,
    id: "",
    expense: null,
  });
  const [deleting, setDeleting] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  const refreshSignal = useMemo(
    () => location.state?.refreshExpenses || location.state?.refresh,
    [location.state]
  );

  const nomeUsuario = useMemo(() => readAuth(AUTH_KEYS.USER_NAME) || "", []);

  const showNotice = useCallback((payload) => {
    setNotice({
      type: payload.type || "info",
      title: payload.title || getNoticeTitle(payload.type || "info"),
      message: payload.message,
      actionLabel: payload.actionLabel,
      onAction: payload.onAction,
      id: Date.now(),
    });

    window.requestAnimationFrame(() => {
      noticeRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  }, []);

  const authOrRedirect = useCallback(() => {
    const token = readAuth(AUTH_KEYS.TOKEN);
    const userId = readAuth(AUTH_KEYS.USER_ID);

    if (!token || !userId) {
      showNotice({
        type: "warning",
        title: "Sessão expirada",
        message: "Faça login novamente para acessar suas despesas.",
        actionLabel: "Ir para login",
        onAction: () => navigate("/login", { replace: true }),
      });
      return null;
    }

    return { token, userId };
  }, [navigate, showNotice]);

  const fetchExpenses = useCallback(
    async ({ silent = false } = {}) => {
      const auth = authOrRedirect();

      if (!auth) {
        setLoading(false);
        return;
      }

      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      try {
        const response = await fetch(
          `${API_BASE}/psy/payment`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${auth.token}`,
              "Content-Type": "application/json",
            },
          }
        );

        saveAuthToken(response.headers.get("x-new-token"));

        if (response.status === 401 || response.status === 403) {
          showNotice({
            type: "warning",
            title: "Sessão expirada",
            message: "Faça login novamente para atualizar suas despesas.",
            actionLabel: "Ir para login",
            onAction: () => navigate("/login", { replace: true }),
          });
          return;
        }

        if (response.status === 404) {
          setItems([]);
          setLastUpdatedAt(new Date());
          return;
        }

        if (!response.ok) {
          const message = await readErrorMessage(response);
          showNotice({
            type: "warning",
            title: "Não foi possível carregar despesas",
            message,
            actionLabel: "Tentar novamente",
            onAction: () => fetchExpenses({ silent: true }),
          });
          setItems([]);
          return;
        }

        const data = await response.json();
        const list = normalizeApiList(data).filter(isExpenseRecord);

        setItems(list);
        setLastUpdatedAt(new Date());

        if (silent) {
          showNotice({
            type: "success",
            title: "Lista atualizada",
            message: "As despesas foram atualizadas com sucesso.",
          });
        }
      } catch (error) {
        console.error("Erro ao buscar despesas:", error);
        showNotice({
          type: "warning",
          title: "Falha de comunicação",
          message: "Não conseguimos conversar com a API agora. Verifique sua conexão e tente novamente.",
          actionLabel: "Tentar novamente",
          onAction: () => fetchExpenses({ silent: true }),
        });
        setItems([]);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [authOrRedirect, navigate, showNotice]
  );

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  useEffect(() => {
    if (!refreshSignal) return;

    fetchExpenses({ silent: true });
    navigate(location.pathname, { replace: true, state: {} });
  }, [refreshSignal, fetchExpenses, navigate, location.pathname]);

  const categories = useMemo(() => {
    const set = new Set(items.map(getExpenseCategory).filter(Boolean));
    return ["Todas", ...Array.from(set).sort((a, b) => a.localeCompare(b, "pt-BR"))];
  }, [items]);

  const filteredItems = useMemo(() => {
    const q = normalizeText(query);

    const filtered = items.filter((expense) => {
      const matchesQuery = !q || getExpenseSearchText(expense).includes(q);
      const matchesCategory =
        categoryFilter === "Todas" || getExpenseCategory(expense) === categoryFilter;

      return matchesQuery && matchesCategory;
    });

    return [...filtered].sort((a, b) => {
      const valueA = getExpenseValue(a);
      const valueB = getExpenseValue(b);

      const dateA = parseDate(getExpenseDate(a))?.getTime() || 0;
      const dateB = parseDate(getExpenseDate(b))?.getTime() || 0;

      const installmentsA = getExpenseInstallments(a).length;
      const installmentsB = getExpenseInstallments(b).length;

      if (sortBy === "date-asc") return dateA - dateB;
      if (sortBy === "value-desc") return valueB - valueA;
      if (sortBy === "value-asc") return valueA - valueB;
      if (sortBy === "next-due") return getNextDueTime(a) - getNextDueTime(b);
      if (sortBy === "installments-desc") return installmentsB - installmentsA;

      return dateB - dateA;
    });
  }, [items, query, categoryFilter, sortBy]);

  const metrics = useMemo(() => {
    const total = items.reduce((sum, item) => sum + getExpenseValue(item), 0);
    const currentMonth = items
      .filter((item) => isCurrentMonth(getExpenseDate(item)))
      .reduce((sum, item) => sum + getExpenseValue(item), 0);

    const installmentsCount = items.reduce(
      (sum, item) => sum + getExpenseInstallments(item).length,
      0
    );

    const openInstallmentsTotal = items.reduce((sum, item) => {
      const next = getNextInstallment(item);
      return sum + (next && !isInstallmentPaid(next) ? getInstallmentValue(next) : 0);
    }, 0);

    const average = items.length ? total / items.length : 0;

    return {
      total,
      currentMonth,
      installmentsCount,
      openInstallmentsTotal,
      average,
      count: items.length,
    };
  }, [items]);

  const askDelete = (expense) => {
    const id = getExpenseId(expense);

    if (!id) {
      showNotice({
        type: "warning",
        title: "Despesa inválida",
        message: "Não foi possível identificar a despesa selecionada.",
      });
      return;
    }

    setConfirmDel({
      open: true,
      id,
      expense,
    });
  };

  const closeDelete = () => {
    if (deleting) return;
    setConfirmDel({ open: false, id: "", expense: null });
  };

  const doDelete = async () => {
    const id = confirmDel.id;

    if (!id) {
      closeDelete();
      return;
    }

    const auth = authOrRedirect();
    if (!auth) return;

    setDeleting(true);

    try {
      const response = await fetch(
        `${API_BASE}/psy/payment/${encodeURIComponent(id)}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${auth.token}`,
            "Content-Type": "application/json",
          },
        }
      );

      saveAuthToken(response.headers.get("x-new-token"));

      if (response.status === 401 || response.status === 403) {
        showNotice({
          type: "warning",
          title: "Sessão expirada",
          message: "Faça login novamente para excluir despesas.",
          actionLabel: "Ir para login",
          onAction: () => navigate("/login", { replace: true }),
        });
        return;
      }

      if (!response.ok) {
        const message = await readErrorMessage(response);
        showNotice({
          type: "warning",
          title: "Não foi possível excluir",
          message,
        });
        return;
      }

      showNotice({
        type: "success",
        title: "Despesa excluída",
        message: "A despesa foi removida com sucesso.",
      });
      setItems((prev) => prev.filter((item) => getExpenseId(item) !== id));
      setConfirmDel({ open: false, id: "", expense: null });
    } catch (error) {
      console.error("Erro ao excluir despesa:", error);
      showNotice({
        type: "warning",
        title: "Falha ao excluir",
        message: "Não conseguimos excluir a despesa agora. Tente novamente.",
      });
    } finally {
      setDeleting(false);
    }
  };

  const goNew = () => navigate("/finance/expense/new");
  const goBack = () => navigate("/finance");
  const goEdit = (expense) => navigate("/finance/expense/edit", { state: { expense } });

  const clearFilters = () => {
    setQuery("");
    setCategoryFilter("Todas");
  };

  return (
    <div className="explist-page">
      <div className="explist-bg" aria-hidden="true">
        <span className="explist-bg__orb explist-bg__orb--one" />
        <span className="explist-bg__orb explist-bg__orb--two" />
        <span className="explist-bg__orb explist-bg__orb--three" />
      </div>

      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <header className="explist-topbar">
        <button
          className="explist-icon-btn"
          type="button"
          onClick={() => setSidebarOpen(true)}
          aria-label="Abrir menu"
        >
          ☰
        </button>

        <div className="explist-brand">
          <img src={Logo} alt="PsyAssist" className="explist-brand__logo" />
          <div className="explist-brand__text">
            <strong>Minhas despesas</strong>
            <span>Financeiro • {nomeUsuario || "Usuário"}</span>
          </div>
        </div>

        <div className="explist-top-actions">
          <button
            className="explist-btn explist-btn--ghost explist-btn--desktop"
            type="button"
            onClick={goBack}
          >
            Voltar
          </button>

          <button
            className="explist-btn explist-btn--ghost"
            type="button"
            onClick={() => fetchExpenses({ silent: true })}
            disabled={loading || refreshing}
          >
            {refreshing ? "Atualizando..." : "Atualizar"}
          </button>

          <button
            className="explist-btn explist-btn--primary"
            type="button"
            onClick={goNew}
          >
            Nova despesa
          </button>
        </div>
      </header>

      <main className="explist-main">
        <div ref={noticeRef}>
          <NoticeBanner notice={notice} onClose={() => setNotice(null)} />
        </div>

        <section className="explist-hero">
          <div>
            <span className="explist-kicker">Controle de saídas</span>
            <h1>Despesas</h1>
            <p>
              Acompanhe custos, parcelas e próximos vencimentos em uma visão
              clara para manter o financeiro previsível.
            </p>
          </div>

          <aside className="explist-hero-card">
            <div className="explist-hero-card__icon" aria-hidden="true">
              💸
            </div>
            <div>
              <small>Total registrado</small>
              <strong>{moneyBR(metrics.total)}</strong>
              <span>{metrics.count} despesa(s) cadastrada(s)</span>
            </div>
          </aside>
        </section>

        <section className="explist-metrics">
          <article className="explist-metric explist-metric--danger">
            <span>Despesas do mês</span>
            <strong>{moneyBR(metrics.currentMonth)}</strong>
            <small>Mês atual</small>
          </article>

          <article className="explist-metric explist-metric--blue">
            <span>Média</span>
            <strong>{moneyBR(metrics.average)}</strong>
            <small>Valor médio por despesa</small>
          </article>

          <article className="explist-metric explist-metric--amber">
            <span>Parcelas</span>
            <strong>{metrics.installmentsCount}</strong>
            <small>Total registrado</small>
          </article>

          <article className="explist-metric explist-metric--green">
            <span>Próximos valores</span>
            <strong>{moneyBR(metrics.openInstallmentsTotal)}</strong>
            <small>Soma da próxima parcela de cada despesa</small>
          </article>
        </section>

        <section className="explist-panel">
          <div className="explist-panel__head">
            <div>
              <span>Lista financeira</span>
              <h2>Despesas registradas</h2>
              <p>
                {loading
                  ? "Carregando lançamentos..."
                  : `${filteredItems.length} de ${items.length} despesa(s) exibida(s)`}
              </p>
            </div>

            {lastUpdatedAt ? (
              <div className="explist-updated">
                Atualizado às {getTimeBR(lastUpdatedAt)}
              </div>
            ) : null}
          </div>

          <div className="explist-toolbar">
            <label className="explist-search">
              <span aria-hidden="true">⌕</span>
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Buscar categoria, descrição, valor, data ou vencimento..."
                aria-label="Buscar despesa"
              />

              {query ? (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  aria-label="Limpar busca"
                >
                  ×
                </button>
              ) : null}
            </label>

            <label className="explist-select">
              <span>Categoria</span>
              <select
                value={categoryFilter}
                onChange={(event) => setCategoryFilter(event.target.value)}
                aria-label="Filtrar por categoria"
              >
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </label>

            <label className="explist-select">
              <span>Ordenar</span>
              <select
                value={sortBy}
                onChange={(event) => setSortBy(event.target.value)}
                aria-label="Ordenar despesas"
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {loading ? (
            <ExpenseSkeleton />
          ) : filteredItems.length === 0 ? (
            <div className="explist-state">
              <div className="explist-state__icon">💸</div>

              <strong>
                {items.length ? "Nenhuma despesa encontrada." : "Nenhuma despesa registrada."}
              </strong>

              <span>
                {items.length
                  ? "Ajuste os filtros ou limpe a busca para ver mais resultados."
                  : "Cadastre sua primeira despesa para começar o controle financeiro."}
              </span>

              <div className="explist-state__actions">
                {items.length ? (
                  <button
                    className="explist-btn explist-btn--ghost-dark"
                    type="button"
                    onClick={clearFilters}
                  >
                    Limpar filtros
                  </button>
                ) : null}

                <button
                  className="explist-btn explist-btn--primary"
                  type="button"
                  onClick={goNew}
                >
                  Nova despesa
                </button>
              </div>
            </div>
          ) : (
            <div className="explist-list">
              {filteredItems.map((expense) => {
                const id = getExpenseId(expense);
                const total = getExpenseValue(expense);
                const category = getExpenseCategory(expense);
                const categoryMeta = getCategoryMeta(category);
                const date = getExpenseDate(expense);
                const description = getExpenseDescription(expense);
                const installments = getExpenseInstallments(expense);
                const nextInstallment = getNextInstallment(expense);

                return (
                  <article className="explist-item" key={id}>
                    <div
                      className={`explist-icon explist-icon--${categoryMeta.tone}`}
                      aria-hidden="true"
                    >
                      {categoryMeta.icon}
                    </div>

                    <div className="explist-content">
                      <div className="explist-title-row">
                        <div>
                          <strong>{moneyBR(total)}</strong>
                          <span>{categoryMeta.label || category}</span>
                        </div>

                        <small>{formatMonthBR(date)}</small>
                      </div>

                      <div className="explist-meta">
                        {description || "Sem descrição informada"}
                      </div>

                      <div className="explist-submeta">
                        <span>
                          Criada em <b>{formatDateBR(date)}</b>
                        </span>

                        {installments.length > 0 && nextInstallment ? (
                          <span>
                            Parcelas: <b>{installments.length}</b> • Próx.:{" "}
                            <b>{moneyBR(getInstallmentValue(nextInstallment))}</b>{" "}
                            em <b>{formatDateBR(getInstallmentDueDate(nextInstallment))}</b>
                          </span>
                        ) : (
                          <span>Sem parcelas registradas</span>
                        )}
                      </div>
                    </div>

                    <div className="explist-menu">
                      <button
                        className="explist-link"
                        type="button"
                        onClick={() => goEdit(expense)}
                      >
                        Editar
                      </button>

                      <button
                        className="explist-link explist-link--danger"
                        type="button"
                        onClick={() => askDelete(expense)}
                      >
                        Excluir
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </main>

      <div className="explist-bottom-actions" aria-label="Ações rápidas de despesas">
        <button className="explist-bottom-actions__btn" type="button" onClick={goBack}>
          Voltar
        </button>

        <button
          className="explist-bottom-actions__btn"
          type="button"
          onClick={() => fetchExpenses({ silent: true })}
          disabled={loading || refreshing}
        >
          {refreshing ? "Atualizando..." : "Atualizar"}
        </button>

        <button
          className="explist-bottom-actions__btn explist-bottom-actions__btn--primary"
          type="button"
          onClick={goNew}
        >
          Nova
        </button>
      </div>

      <button
        className="explist-fab"
        type="button"
        onClick={goNew}
        aria-label="Nova despesa"
      >
        +
      </button>

      <ConfirmModal
        open={confirmDel.open}
        title="Excluir despesa?"
        message="Essa ação remove a despesa e suas parcelas vinculadas. Confirme apenas se realmente deseja continuar."
        detail={
          confirmDel.expense
            ? `${getExpenseCategory(confirmDel.expense)} • ${moneyBR(
                getExpenseValue(confirmDel.expense)
              )}`
            : ""
        }
        loading={deleting}
        onClose={closeDelete}
        onConfirm={doDelete}
      />
    </div>
  );
}
